import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader, Menu, X, ShieldAlert } from 'lucide-react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, appId } from './lib/firebase';
import { createActivityEntry, ACTIVITY_TYPES, toLocalDateString } from './utils/helpers';
import { AnimatePresence } from 'framer-motion';

// Hooks
import { useAuth, useFirestoreData, useAutoMigrateContacts, useCsvUpload, useNotifications, useSocketNotifications, useAppSettings, useContacts, useSources, useLeads, useUsers } from './hooks';
import { leadsService } from './services/api';
import { useFCM } from './hooks/useFCM';
import { useLanguage } from './contexts/LanguageContext';
import { LOSS_REASONS } from './lib/constants';


// UI Components
import { Toast, DebugPanel, AuthScreen, NotificationBell, LanguageToggle, ProtectedRoute, getDefaultPath, FacebookWarningBanner } from './components/ui';
import RoleSidebar from './components/ui/RoleSidebar';
import AdminLayout from './components/ui/AdminLayout';
import PWAUpdatePrompt from './components/ui/PWAUpdatePrompt';
import Navbar from './components/ui/Navbar';

// Modal Components
import {
  LeadDetailModal,
  RevenueDetailModal,
  NewLeadModal,
  SourceDetailModal,
  UserSettingsPanel,
  ContactsMigrationPanel,
  LogCallModal,
  DailySummaryModal
} from './components/modals';

// View Components
import {
  DashboardView,
  LeadsView,
  ContactsView,
  SourcesView,
  EmployeeView,
  OwnerDashboard,
  FinanceView,
  CallListView,
  ConversationLogsView,
  AuditLogsView,
  AdminPanelView,
  IntegrationsView,
  CalendarView,
  GreetingsLogView
} from './components/views';

// Constants (fallback, prefer useAppSettings)

export default function App() {
  const { t } = useLanguage();
  
  // Auth state
  const { user, activeUser, setActiveUser, authLoading, authError, login, signup, logout, clearTemporaryPassword } = useAuth();

  // FCM Push Notifications — initialise after login, clear on logout
  const { initFCM, clearFCM } = useFCM(activeUser);
  useEffect(() => {
    if (activeUser) {
      // Small delay so the token request doesn't compete with initial page render
      const t = setTimeout(() => initFCM(), 3000);
      return () => clearTimeout(t);
    }
  }, [activeUser, initFCM]);

  // Wrap logout to also clear FCM token
  const handleLogout = async () => {
    await clearFCM();
    await logout();
  };


  // Firestore data
  // Firestore data (DISABLED FOR PERFORMANCE)
  // const {
  //   data,
  //   sources,
  //   contacts,
  //   stats,
  //   handleSaveLead,
  //   handleAddLead,
  //   handleAddSource,
  //   handleUpdateSource,
  //   handleDeleteSource,
  //   handleAddContact,
  //   handleUpdateContact,
  //   addActivityLog,
  //   handleTruncateLeads,
  //   handleDeleteAllContacts
  // } = useFirestoreData(user);

  // Safe fallbacks for disabled firestore vars
  const data = [];
  const sources = [];
  const contacts = [];
  const stats = { revenue: 0, pipe: 0, total: 0, conversion: 0, wonCount: 0 };
  const handleSaveLead = async () => { };
  const handleAddLead = async () => { };
  const handleAddSource = async () => { };
  const handleUpdateSource = async () => { };
  const handleDeleteSource = async () => { };
  const handleAddContact = async () => { };
  const handleUpdateContact = async () => { };
  const addActivityLog = async () => { };
  const handleTruncateLeads = async () => { };
  const handleDeleteAllContacts = async () => { };

  // App settings (managers, event types from Firestore)
  const { eventTypes, venues } = useAppSettings();

  // Users from API (for managers list)
  const { users: apiUsers } = useUsers({ enabled: !!user });
  const managerNames = (apiUsers || [])
    .filter(u => ['sales', 'salesperson', 'manager'].includes((u.role || '').toLowerCase()) && u.is_active !== false)
    .map(u => u.username);

  // Contacts from API
  const {
    contacts: apiContacts,
    loading: contactsLoading,
    addContact,
    updateContact,
    deleteContact,
    deleteAllContacts,
    searchContacts,
    pagination: contactsPagination,
    fetchContacts
  } = useContacts({ enabled: !!user });

  // Sources from API
  const {
    sources: apiSources,
    addSource,
    updateSource,
    deleteSource
  } = useSources({ enabled: !!user });

  // Leads from API
  const {
    leads: apiLeads,
    stats: leadsStats,
    loading: leadsLoading,
    pagination: leadsPagination,
    fetchLeads,
    addLead,
    updateLead,
    patchLead,
    deleteLead,
    deleteAllLeads,
    addLeadNote,
    hasMore: hasMoreLeads,
    loadMoreLeads,
    fetchAnalytics,
    analyticsLoading,
  } = useLeads({ enabled: !!user });

  // CSV upload — placed after useLeads so fetchLeads is already declared
  const { importProgress, closeImportModal, handleFileUpload } = useCsvUpload(apiSources, fetchLeads);

  // Notifications
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    requestBrowserPermission,
    handleNewNotification,
    loadMore: loadMoreNotifications,
    hasMore: hasMoreNotifications,
    totalNotifications,
    loadingMore: loadingMoreNotifications
  } = useNotifications(activeUser, { enableBrowserNotifications: true });

  // Real-time Socket Notifications
  // Track new leads for highlighting
  const [newLeadIds, setNewLeadIds] = useState(() => {
    // Initialize from localStorage if available
    try {
      const saved = localStorage.getItem('newLeadIds');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error parsing newLeadIds from localStorage', e);
      return [];
    }
  });

  const [viewedLeadIds, setViewedLeadIds] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Persist newLeadIds to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('newLeadIds', JSON.stringify(newLeadIds));
    } catch (e) {
      console.error('Error saving newLeadIds to localStorage', e);
    }
  }, [newLeadIds]);

  useSocketNotifications((payload) => {
    if (payload.type === 'audit_log') {
      setAuditLogs(prev => [payload.data, ...prev]);
      return;
    }

    // Payload can be { lead, notification } or just lead (backward compatibility)
    const newLead = payload.isStandaloneNotification ? null : (payload.lead || payload);
    const notification = payload.notification;

    // 1. Play sound (handled in hook)

    // 2. Add to new ids for highlighting 
    if (newLead && newLead.id) {
      setNewLeadIds(prev => {
        // Avoid duplicates
        if (prev.includes(newLead.id)) return prev;
        return [...prev, newLead.id];
      });
    }

    // 3. Refresh leads data to show the new row
    fetchLeads();

    // 4. Add to notification center (Local state, already saved in DB by server)
    if (notification) {
      // Since socket now only sends to the correct rooms, no extra filter needed.
      // But keep a basic check for safety.
      const isForMe =
        (notification.userId && notification.userId == activeUser?.id) ||
        (notification.assignedTo === activeUser?.role) ||
        (notification.assignedTo === 'all') ||
        ((activeUser?.role === 'Admin' || activeUser?.role === 'Owner') && (notification.type === 'lead_assigned' || notification.type === 'sla_breach' || notification.type === 'call_overdue' || notification.type === 'daily_summary'));

      if (isForMe) {
        handleNewNotification(notification);
      }
    }
  }, activeUser);

  // Remove from new list when viewed
  const handleLeadView = (lead) => {
    if (newLeadIds.includes(lead.id)) {
      setNewLeadIds(prev => prev.filter(id => id !== lead.id));
      setViewedLeadIds(prev => [...prev, lead.id]);
    }
    setSelectedLead(lead);
  };

  const handleLeadMarkAsViewed = (lead) => {
    if (newLeadIds.includes(lead.id)) {
      setNewLeadIds(prev => prev.filter(id => id !== lead.id));
      setViewedLeadIds(prev => [...prev, lead.id]);
    }
  };

  // UI state
  // UI state
  // const [activeTab, setActiveTab] = useState('owner-dashboard'); // Replaced by Router
  // const [selectedLead, setSelectedLead] = useState(null); // Replaced by URL state
  // const [showRevenue, setShowRevenue] = useState(false); // Replaced by URL state
  // const [showAdmin, setShowAdmin] = useState(false); // Replaced by URL state
  // const [showIntegrationsPanel, setShowIntegrationsPanel] = useState(false); // Replaced by URL state
  // const [showNewLead, setShowNewLead] = useState(false); // Replaced by SearchParams
  // const [selectedSource, setSelectedSource] = useState(null); // Replaced by URL state
  // const [showUserSettings, setShowUserSettings] = useState(false); // Replaced by URL state
  // const [showMigrationPanel, setShowMigrationPanel] = useState(false); // Replaced by URL state
  const [toast, setToast] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuAnimating, setMenuAnimating] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      setMenuVisible(true);
      requestAnimationFrame(() => setMenuAnimating(true));
    } else {
      setMenuAnimating(false);
      const t = setTimeout(() => setMenuVisible(false), 280);
      return () => clearTimeout(t);
    }
  }, [mobileMenuOpen]);
  const [userSettingsDefaultTab, setUserSettingsDefaultTab] = useState('profile');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch { return false; }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', String(next)); } catch { }
      return next;
    });
  };

  // Router Helpers
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Derived state from URL for Modals
  const showNewLead = searchParams.get('modal') === 'new-lead';
  const showRevenue = searchParams.get('modal') === 'revenue';
  const showUserSettings = searchParams.get('modal') === 'settings';
  const showMigrationPanel = searchParams.get('modal') === 'migration';
  const showLogCallModal = searchParams.get('modal') === 'log-call';
  const showDailySummaryModal = searchParams.get('modal') === 'daily-summary';

  const selectedLeadId = searchParams.get('leadId');
  const selectedSourceId = searchParams.get('sourceId');

  const [localSelectedLead, setLocalSelectedLead] = useState(null);

  const foundLead = selectedLeadId
    ? (localSelectedLead?.id == selectedLeadId ? localSelectedLead : (apiLeads.find(l => l.id == selectedLeadId) || data.find(l => l.id == selectedLeadId)))
    : null;

  useEffect(() => {
    if (selectedLeadId && !foundLead) {
      leadsService.getById(selectedLeadId).then(rawLead => {
         const transformedLead = {
            ...rawLead,
            clientName: rawLead.contact ? [rawLead.contact.firstName, rawLead.contact.lastName].filter(Boolean).join(' ') : rawLead.title || 'Unknown',
            stage: rawLead.status,
            amount: rawLead.finalAmount || rawLead.quotationAmount || rawLead.clientBudget || 0,
            inquiryDate: toLocalDateString(rawLead.createdAt),
            manager: rawLead.assignee ? rawLead.assignee.username : 'Unassigned',
            phone: rawLead.contact?.phone || '',
            source: rawLead.source?.name || ''
         };
         setLocalSelectedLead(transformedLead);
      }).catch(console.error);
    }
  }, [selectedLeadId, foundLead]);

  const selectedLead = foundLead;

  const selectedSource = selectedSourceId
    ? (apiSources.find(s => s.id == selectedSourceId) || sources.find(s => s.id == selectedSourceId))
    : null;

  // Helpers to update URL state
  const updateModalState = (modalName, show) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (show) newParams.set('modal', modalName);
      else if (newParams.get('modal') === modalName) newParams.delete('modal');
      return newParams;
    });
  };

  const setShowNewLead = (show) => updateModalState('new-lead', show);
  const setShowRevenue = (show) => updateModalState('revenue', show);
  const setShowAdmin = (show) => updateModalState('admin', show);
  const setShowIntegrationsPanel = (show) => updateModalState('integrations', show);
  const setShowUserSettings = (show) => updateModalState('settings', show);
  const setShowMigrationPanel = (show) => updateModalState('migration', show);
  const setShowLogCallModal = (show) => updateModalState('log-call', show);
  const setShowDailySummaryModal = (show) => updateModalState('daily-summary', show);

  const setSelectedLead = (lead) => {
    setLocalSelectedLead(lead);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (lead) newParams.set('leadId', lead.id);
      else newParams.delete('leadId');
      return newParams;
    });
  };

  const setSelectedSource = (source) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (source) newParams.set('sourceId', source.id);
      else newParams.delete('sourceId');
      return newParams;
    });
  };

  const activeTab = location.pathname === '/' ? 'owner-dashboard' : location.pathname.substring(1);
  const setActiveTab = (tab) => {
    const path = tab === 'owner-dashboard' ? '/' : `/${tab}`;
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Debug state
  const [debugLogs, setDebugLogs] = useState([]);

  // Auto-migrate contacts
  useAutoMigrateContacts(activeUser, data, contacts, setToast);

  // Wrapper for adding lead to handle name-to-ID mapping
  const handleAddLeadWrapper = async (leadData) => {
    try {
      // Find manager ID. If empty (auto-assign), set to null.
      let assignedTo = null;
      if (leadData.manager && leadData.manager !== '') {
        const managerUser = apiUsers.find(u => u.username === leadData.manager);
        assignedTo = managerUser ? managerUser.id : null;
      }

      // Find source ID
      const sourceObj = apiSources.find(s => s.name === leadData.source);
      const sourceId = sourceObj ? sourceObj.id : null;

      // Construct title from event type and date if available
      let title = leadData.title;
      if (!title && leadData.eventType) {
        title = `${leadData.eventType}${leadData.eventDate ? ` - ${leadData.eventDate}` : ''}`;
      }

      // Construct clean payload matching Joi schema
      // Ensure status is valid
      const status = (leadData.stage === 'New Lead' ? 'New' : leadData.stage) || 'New';

      const payload = {
        quotationAmount: Number(leadData.quotationAmount) || 0,
        clientBudget: Number(leadData.clientBudget) || 0,
        status,
        notes: leadData.notes,
        contactId: leadData.contactId,
        assignedTo,
        sourceId,
        title,
        // Map inquiryDate to expectedCloseDate if provided, or leave undefined
        expectedCloseDate: leadData.inquiryDate ? new Date(leadData.inquiryDate).toISOString() : null,
        // Event details
        eventDate: leadData.eventDate ? new Date(leadData.eventDate).toISOString() : null,
        eventType: leadData.eventType,
        venue: leadData.venue,
        guests: leadData.guests ? parseInt(leadData.guests) : null
      };

      const newLead = await addLead(payload);

      // Highlight the new lead for the creator immediately
      if (newLead && newLead.id) {
        setNewLeadIds(prev => {
          if (prev.includes(newLead.id)) return prev;
          return [...prev, newLead.id];
        });
        // NOTE: Do NOT manually inject a local notification here.
        // The real notification is sent via the socket (role:Admin broadcast)
        // so the Admin will receive the correct "assigned to SalesX" notification automatically.
        // Adding one here would cause a duplicate "assigned to you" every time.
      }

      setShowNewLead(false);
      setToast({ message: 'Lead added successfully', type: 'success' });
    } catch (error) {
      console.error('Error adding lead:', error);
      setToast({ message: error.message || 'Failed to add lead', type: 'error' });
    }
  };


  // Wrapper for updating lead to handle field name mapping
  const handleUpdateLeadWrapper = async (id, updateData) => {
    try {
      // Map frontend field names to backend schema
      const payload = { ...updateData };

      // Map 'stage' to 'status'
      if (payload.stage) {
        payload.status = payload.stage;
        delete payload.stage;
      }

      // Map source name to sourceId
      if (payload.source && typeof payload.source === 'string') {
        const sourceObj = apiSources.find(s => s.name === payload.source);
        payload.sourceId = sourceObj ? sourceObj.id : null;
        delete payload.source;
      }

      // Map manager name to assignedTo (user ID)
      if (payload.manager && typeof payload.manager === 'string') {
        const managerObj = apiUsers.find(u => u.username === payload.manager);
        payload.assignedTo = managerObj ? managerObj.id : null;
        delete payload.manager;
      }

      // Remove frontend-only fields that don't exist in backend schema
      delete payload.id; // Prisma error: id is in where clause, not data
      delete payload.contactId; // Can't update foreign key directly
      delete payload.clientName;
      delete payload.phone;
      delete payload.source; // Already mapped to sourceId
      delete payload.inquiryDate;
      delete payload.stageUpdatedAt;
      delete payload.stageUpdatedBy;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.contact;
      delete payload.assignee;
      delete payload.activityLog;
      delete payload.totalPaid;
      delete payload.totalDue;
      
      if (payload.stage) {
        payload.status = payload.stage;
        delete payload.stage;
      }

      // Sanitize date fields: empty string '' is NOT a valid date for Joi/Prisma
      // Convert empty strings to null so server validation passes
      const dateFields = ['eventDate', 'siteVisitDate', 'bookedAt', 'expectedCloseDate'];
      dateFields.forEach(field => {
        if (payload[field] === '' || payload[field] === undefined) {
          payload[field] = null;
        }
      });
      
      // Note: guests, venue, eventType, eventDate, finalAmount, advanceAmount, 
      // siteVisitDate, siteVisitTime, bookingNotes, bookedAt, bookedBy are valid database fields.

      await updateLead(id, payload);
      setToast({ message: 'Lead updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error updating lead:', error);
      setToast({ message: error.message || 'Failed to update lead', type: 'error' });
    }
  };

  const handleDeleteLeadWrapper = async (id) => {
    try {
      await deleteLead(id);
      setSelectedLead(null);
      setToast({ message: 'Lead deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting lead:', error);
      setToast({ message: error.message || 'Failed to delete lead', type: 'error' });
    }
  };

  // Handle stage change from employee view with activity logging
  const handleStageChange = async (leadId, newStage, oldStage = null) => {
    try {
      // 1. Update lead stage
      await updateLead(leadId, {
        status: newStage
      });

      // 2. Add log entry
      const activityData = { from: oldStage || 'Unknown', to: newStage };
      await addLeadNote(leadId,
        JSON.stringify(activityData),
        ACTIVITY_TYPES.STAGE_CHANGE
      );

      // Notify open modals (like LeadDetailModal) to refresh their timeline
      window.dispatchEvent(new CustomEvent('lead_timeline_updated', { detail: { leadId } }));

      setToast({ message: `Lead moved to ${newStage}`, type: 'success' });
    } catch (error) {
      console.error('Error updating stage:', error);
      setToast({ message: 'Failed to update stage', type: 'error' });
    }
  };

  // Log WhatsApp click activity
  const handleWhatsAppClick = async (leadId) => {
    try {
      await addLeadNote(leadId,
        JSON.stringify({}),
        ACTIVITY_TYPES.WHATSAPP_OPENED
      );
    } catch (error) {
      console.error('Error logging WhatsApp activity:', error);
    }
  };

  // Handle call logging from CallListView — uses dedicated lightweight endpoint
  const handleLogCall = async (leadId, callData) => {
    // ── 1. OPTIMISTIC UPDATE — instantly reflect in UI ──────────────────────
    const optimisticPatch = {
      lastCallDate: new Date().toISOString(),
      lastCallOutcome: callData.outcome,
      lastCallNotes: callData.notes || null,
      lastCalledBy: activeUser?.name,
      nextCallDate: callData.nextCallDate || null,
      nextCallTime: callData.nextCallDate ? (callData.nextCallTime || null) : null,
      ...(callData.stage && { stage: callData.stage, status: callData.stage }),
    };

    // Patch local state immediately so UI feels instant
    patchLead(leadId, optimisticPatch);

    try {
      // ── 2. FIRE dedicated call-log endpoint (no JOINs on server) ───────────
      await leadsService.logCall(leadId, {
        outcome: callData.outcome,
        notes: callData.notes || null,
        nextCallDate: callData.nextCallDate || null,
        nextCallTime: callData.nextCallTime || null,
        calledBy: activeUser?.name,
        ...(callData.stage && { stage: callData.stage }),
        ...(callData.leadNotes && { leadNotes: callData.leadNotes }),
      });

      // ── 3. Also save Requirements & Quote fields (quotationItems, venue, eventDate, etc.) ──
      const leadDetailFields = [
        'quotationItems', 'quotationAmount', 'eventDate', 'eventType', 'venue',
        'guests', 'shift', 'clientBudget', 'siteVisitDate', 'siteVisitTime',
        'siteVisited', 'clientCnic', 'clientAddress', 'bookingNotes',
        'bookedAt', 'bookedBy',
      ];
      const leadDetailsPayload = {};
      leadDetailFields.forEach(key => {
        if (callData[key] !== undefined && callData[key] !== null && callData[key] !== '') {
          leadDetailsPayload[key] = callData[key];
        }
      });
      // Also include stage as status if present
      if (callData.stage) {
        leadDetailsPayload.status = callData.stage;
      }
      if (Object.keys(leadDetailsPayload).length > 0) {
        updateLead(leadId, leadDetailsPayload).catch(err =>
          console.warn('Lead details update error (non-critical):', err)
        );
      }

      // ── 4. Stage-change activity note — fire & forget (non-blocking) ───────
      if (callData.stage) {
        const currentLead = apiLeads.find(l => l.id === leadId);
        if (currentLead && currentLead.stage !== callData.stage) {
          let lossLabel = undefined;
          if (callData.stage === 'Lost' && callData.lostReason) {
            lossLabel = LOSS_REASONS.find(r => r.id === callData.lostReason)?.label || callData.lostReason;
          }

          const activityData = { 
            from: currentLead.stage, 
            to: callData.stage,
            lossReason: lossLabel,
            note: callData.lostReasonNote || undefined
          };

          addLeadNote(
            leadId,
            JSON.stringify(activityData),
            ACTIVITY_TYPES.STAGE_CHANGE
          ).catch(err => console.warn('Stage note error (non-critical):', err));
        }
      }

      setToast({ message: 'Call logged successfully', type: 'success' });
    } catch (error) {
      console.error('Error logging call:', error);
      setToast({ message: 'Failed to log call', type: 'error' });
    }
  };

  // Handle tab change (close mobile menu)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  const handleNotificationClick = (notif) => {
    if (notif.leadId) {
      const lead = apiLeads.find(l => l.id === notif.leadId);
      if (lead) {
        if (notif.type === 'sla_breach' || notif.type === 'stale_lead_reminder' || notif.type === 'stale_lead_escalation' || notif.type === 'upcoming_call' || notif.type === 'call_overdue') {
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('leadId', lead.id);
            newParams.set('modal', 'log-call');
            return newParams;
          });
        } else {
          setSelectedLead(lead);
        }
      }
    } else if (notif.type === 'system_alert' || notif.type === 'daily_summary') {
      // It's a daily summary, open Daily Summary Modal instead
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('modal', 'daily-summary');
        // Store message temporarily in state or use a generic approach 
        // We will just let the modal read from the notification directly, or we can set it in 
        // a dedicated state.
        return newParams;
      });
      // Set the notification content to local state to display in modal
      setDailySummaryText(notif.message);
      setDailySummaryDate(notif.createdAt);
    }
  };

  const [dailySummaryText, setDailySummaryText] = useState('');
  const [dailySummaryDate, setDailySummaryDate] = useState(null);

  // Loading state
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center flex-col gap-4">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-500">Authenticating...</p>
      </div>
    );
  }

  // Auth screen (show if not logged in, not authorized, or no active user)
  if (!user || !activeUser || authError) {
    return (
      <>
        <AuthScreen onLogin={setActiveUser} isLoading={authLoading} authError={authError} login={login} signup={signup} logout={logout} />
        {/* <DebugPanel logs={debugLogs} /> */}
      </>
    );
  }

  // Check if we're on the admin route — render separate layout for it
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/') || location.pathname === '/integrations';

  // ── Admin Route: Separate full-page layout ──
  if (isAdminRoute) {
    return (
      <div className="flex flex-col h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
        <Navbar
          activeUser={activeUser}
          notifications={notifications}
          onMarkRead={markAsRead}
          onMarkAllRead={markAllAsRead}
          onDeleteNotification={deleteNotification}
          onNotificationClick={handleNotificationClick}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          hasMore={hasMoreNotifications}
          loadMore={loadMoreNotifications}
          loadingMore={loadingMoreNotifications}
          totalCount={totalNotifications}
        />
        {/* Toast (global) */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
        <Routes>
          <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin']} activeUser={activeUser} />}>
            <Route
              path="/admin"
              element={
                <AdminLayout
                  activeUser={activeUser}
                  mobileMenuOpen={mobileMenuOpen}
                  setMobileMenuOpen={setMobileMenuOpen}
                >
                  <AdminPanelView />
                </AdminLayout>
              }
            />
            <Route
              path="/integrations"
              element={
                <AdminLayout
                  activeUser={activeUser}
                  mobileMenuOpen={mobileMenuOpen}
                  setMobileMenuOpen={setMobileMenuOpen}
                >
                  <IntegrationsView />
                </AdminLayout>
              }
            />
          </Route>
          {/* Redirect any other /admin/* paths */}
          <Route path="*" element={<Navigate to={getDefaultPath(activeUser?.role)} replace />} />
        </Routes>
      </div>
    );
  }

  // ── Main App Layout ──
  return (
    <div className="flex flex-col h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      <Navbar
        activeUser={activeUser}
        notifications={notifications}
        onMarkRead={markAsRead}
        onMarkAllRead={markAllAsRead}
        onDeleteNotification={deleteNotification}
        onNotificationClick={handleNotificationClick}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        hasMore={hasMoreNotifications}
        loadMore={loadMoreNotifications}
        loadingMore={loadingMoreNotifications}
        totalCount={totalNotifications}
      />
      <div className="flex flex-1 overflow-hidden relative">
        <PWAUpdatePrompt />
        {/* <DebugPanel logs={debugLogs} /> */}

        {/* Toast */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        {/* Modals */}
        {selectedLead && !showLogCallModal && (
          <LeadDetailModal
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
            onSave={handleUpdateLeadWrapper}
            currentUser={activeUser}
            sources={apiSources}
            managers={managerNames}
            eventTypes={eventTypes}
            venues={venues}
            onDelete={handleDeleteLeadWrapper}
            onOpenAi={() => { }}
          />
        )}
        {showLogCallModal && selectedLead && (
          <LogCallModal
            lead={selectedLead}
            currentUser={activeUser}
            onClose={() => {
              setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.delete('modal');
                newParams.delete('leadId');
                return newParams;
              });
            }}
            onSave={handleLogCall}
            onSaveLead={handleUpdateLeadWrapper}
            venues={venues}
            eventTypes={eventTypes}
            managers={managerNames}
          />
        )}
        <AnimatePresence>
          {showDailySummaryModal && (
            <DailySummaryModal
              key="daily-summary-modal"
              summaryText={dailySummaryText}
              date={dailySummaryDate}
              onClose={() => {
                setShowDailySummaryModal(false);
              }}
            />
          )}
        </AnimatePresence>
        {showRevenue && <RevenueDetailModal defaultRange={searchParams.get('range')} onClose={() => setShowRevenue(false)} allLeads={apiLeads} />}
        {showNewLead && (
          <NewLeadModal
            onClose={() => setShowNewLead(false)}
            onSave={handleAddLeadWrapper}
            managers={managerNames}
            contacts={apiContacts}
            onAddContact={addContact}
            sources={apiSources}
            eventTypes={eventTypes}
            venues={venues}
            currentUser={activeUser}
          />
        )}
        {selectedSource && (
          <SourceDetailModal
            source={selectedSource}
            onClose={() => setSelectedSource(null)}
            onSelectLead={(lead) => {
              setSelectedSource(null);
              setSelectedLead(lead);
            }}
          />
        )}
        {showUserSettings && (
          <UserSettingsPanel
            currentUser={activeUser}
            onClose={() => setShowUserSettings(false)}
            onLogout={logout}
            defaultTab={userSettingsDefaultTab}
            onPasswordChanged={() => {
              clearTemporaryPassword();
              setToast({ message: 'Password changed successfully!', type: 'success' });
            }}
          />
        )}
        {showMigrationPanel && <ContactsMigrationPanel onClose={() => setShowMigrationPanel(false)} />}

        {/* Desktop Sidebar - Hidden on mobile */}
        <div
          className="hidden lg:block shrink-0 border-r bg-white z-20 h-full relative"
          style={{
            width: sidebarCollapsed ? '72px' : '256px',
            transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <RoleSidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            userRole={activeUser?.role || 'Admin'}
            onShowAdmin={() => setShowAdmin(true)}
            onShowIntegrations={() => navigate('/integrations')}
            onShowUserSettings={() => { setUserSettingsDefaultTab('profile'); setShowUserSettings(true); }}
            onShowMigration={() => setShowMigrationPanel(true)}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebar}
          />
        </div>

        {/* Mobile Menu Overlay — animated slide-down */}
        {menuVisible && (
          <div
            className="lg:hidden fixed inset-0 z-30 top-16"
            style={{
              backgroundColor: menuAnimating ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
              transition: 'background-color 280ms ease'
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setMobileMenuOpen(false); }}
          >
            <div
              className="absolute top-0 left-0 right-0 bg-white border-b shadow-lg max-h-[70vh] overflow-y-auto"
              style={{
                transform: menuAnimating ? 'translateY(0)' : 'translateY(-100%)',
                opacity: menuAnimating ? 1 : 0,
                transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 280ms ease'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <RoleSidebar
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                userRole={activeUser?.role || 'Admin'}
                onShowAdmin={() => { setShowAdmin(true); setMobileMenuOpen(false); }}
                onShowIntegrations={() => { navigate('/integrations'); setMobileMenuOpen(false); }}
                onShowUserSettings={() => { setUserSettingsDefaultTab('profile'); setShowUserSettings(true); setMobileMenuOpen(false); }}
                onShowMigration={() => { setShowMigrationPanel(true); setMobileMenuOpen(false); }}
                isMobile={true}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-6 pb-20 bg-slate-50 relative">

          {/* Facebook Disconnected Warning Banner */}
          <FacebookWarningBanner userRole={activeUser?.role} />

          {/* Temporary Password Warning Banner */}
          {activeUser?.must_change_password && (
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 md:p-4 bg-amber-50 border border-amber-300 rounded-xl shadow-sm animate-pulse-once">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0" />
                <p className="text-sm font-medium text-amber-800" dangerouslySetInnerHTML={{ __html: t('tempPasswordWarning') || 'Your account is using a <strong>temporary password</strong>. Please change it to secure your account.' }}>
                </p>
              </div>
              <button
                onClick={() => {
                  setUserSettingsDefaultTab('password');
                  setShowUserSettings(true);
                }}
                className="shrink-0 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-lg transition-colors shadow-sm shadow-amber-200 whitespace-nowrap"
              >
                {t('changePassword') || 'Change Password'}
              </button>
            </div>
          )}
          {/* Desktop Header Controls replaced by Navbar */}

          <Routes>
            {/* ── Owner / Admin only ── */}
            <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin']} activeUser={activeUser} />}>
              <Route path="/" element={
                <OwnerDashboard leads={apiLeads} fetchAnalytics={fetchAnalytics} analyticsLoading={analyticsLoading} onShowRevenue={(range) => {
                  setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('modal', 'revenue');
                    if (range) params.set('range', range);
                    return params;
                  });
                }} />
              } />
              <Route path="/dashboard" element={
                <DashboardView stats={leadsStats} onShowRevenue={(range) => {
                  setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('modal', 'revenue');
                    if (range) params.set('range', range);
                    return params;
                  });
                }} />
              } />
            </Route>

            {/* ── Leads: Owner/Admin full + Sales (own leads only) ── */}
            <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin', 'Sales']} activeUser={activeUser} />}>
              <Route path="/leads" element={
                <LeadsView
                  data={apiLeads}
                  isLoading={leadsLoading}
                  onSelectLead={handleLeadView}
                  onStageChange={handleStageChange}
                  currentUser={activeUser}
                  newLeadIds={newLeadIds}
                  viewedLeadIds={viewedLeadIds}
                  onShowNewLead={() => setShowNewLead(true)}
                  onFileUpload={handleFileUpload}
                  onTruncateLeads={deleteAllLeads}
                  importProgress={importProgress}
                  closeImportModal={closeImportModal}
                  fetchAllLeads={fetchLeads}
                  pagination={leadsPagination}
                  users={apiUsers}
                  totalLeads={leadsStats?.total}
                />
              } />
            </Route>

            {/* ── Owner / Admin only (continued) ── */}
            <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin']} activeUser={activeUser} />}>

              <Route path="/sources" element={
                <SourcesView
                  sources={apiSources}
                  onAdd={addSource}
                  onUpdate={updateSource}
                  onDelete={deleteSource}
                  onSourceClick={setSelectedSource}
                />
              } />
              <Route path="/audit-logs" element={
                <AuditLogsView leads={apiLeads} currentUser={activeUser} users={apiUsers || []} auditLogs={auditLogs} setAuditLogs={setAuditLogs} />
              } />
            </Route>

            {/* ── Sales + Owner / Admin ── */}
            <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin', 'Sales']} activeUser={activeUser} />}>
              <Route path="/conversation-logs" element={
                <ConversationLogsView leads={apiLeads} currentUser={activeUser} />
              } />
              <Route path="/greetings-log" element={
                <GreetingsLogView currentUser={activeUser} />
              } />
              <Route path="/contacts" element={
                <ContactsView
                  contacts={apiContacts}
                  loading={contactsLoading}
                  onAddContact={addContact}
                  onUpdateContact={updateContact}
                  onDeleteContact={deleteContact}
                  onDeleteAllContacts={deleteAllContacts}
                  searchContacts={searchContacts}
                  fetchAllContacts={fetchContacts}
                  pagination={contactsPagination}
                />
              } />
            </Route>

            {/* ── Sales + Finance only (not Admin/Owner) ── */}
            <Route element={<ProtectedRoute allowedRoles={['Sales', 'Finance']} activeUser={activeUser} />}>
              <Route 
                path="/employee" 
                element={
                  <EmployeeView
                    leads={apiLeads}
                    isLoading={leadsLoading}
                    currentUser={activeUser}
                    onSelectLead={handleLeadView}
                    onLeadViewed={handleLeadMarkAsViewed}
                    onStageChange={handleStageChange}
                    onAddNote={() => {}}
                    onShowNewLead={() => setShowNewLead(true)}
                    newLeadIds={newLeadIds}
                    unreadCount={unreadCount}
                    fetchAnalytics={fetchAnalytics}
                  />
                } 
              />
            </Route>

            {/* ── Call List + Calendar: all roles ── */}
            <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin', 'Sales', 'Finance']} activeUser={activeUser} />}>
              <Route path="/call-list" element={
                <CallListView
                  leads={apiLeads}
                  isLoading={leadsLoading}
                  currentUser={activeUser}
                  onLogCall={handleLogCall}
                  onSelectLead={setSelectedLead}
                  onSaveLead={updateLead}
                  venues={venues}
                  eventTypes={eventTypes}
                  managers={managerNames}
                />
              } />
              <Route path="/calendar" element={
                <CalendarView leads={apiLeads} currentUser={activeUser} />
              } />
            </Route>

            {/* ── Finance + Owner / Admin ── */}
            <Route element={<ProtectedRoute allowedRoles={['Owner', 'Admin', 'Finance']} activeUser={activeUser} />}>
              <Route path="/finance" element={
                <FinanceView leads={apiLeads} isLoading={leadsLoading} onSyncPayments={fetchLeads} />
              } />
            </Route>

            {/* Catch-all — redirect to role's default page */}
            <Route path="*" element={<Navigate to={getDefaultPath(activeUser?.role)} replace />} />
          </Routes>

          {/* Debug Footer - Hidden on mobile */}
          {/* <div className="hidden md:block fixed bottom-0 left-0 w-full bg-black text-white text-xs p-1 text-center opacity-50 pointer-events-none">
          Debug: {data.length} Leads | {contacts.length} Contacts | Rev: {stats.revenue} |
          User: {activeUser ? `${activeUser.name} (${activeUser.role})` : 'Loading...'}
        </div> */}
        </main>
      </div>
    </div>
  );
}
